"use client";

import { type DragEvent, useRef, useState } from "react";

import { FILE_TYPE_PRESETS } from "@unreserved/constants";

import { CloudUploadIcon } from "@/app/_components/icons/get-estimates/cloud";
import { cn } from "@/lib/utils";

import { Button } from "./button";

type FileTypePreset = keyof typeof FILE_TYPE_PRESETS;

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  fileTypePreset?: FileTypePreset;
  acceptedFileTypes?: string[];
  customFileTypeLabel?: string;
  maxFileSizeMB?: number;
  multiple?: boolean;
}

export function FileUpload({
  onFilesSelected,
  acceptedFileTypes,
  fileTypePreset = "images",
  customFileTypeLabel,
  maxFileSizeMB = 30,
  multiple = true,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determining which file types to use
  const fileConfig = acceptedFileTypes
    ? {
        extensions: acceptedFileTypes,
        label: customFileTypeLabel || acceptedFileTypes.join(", ").toUpperCase(),
      }
    : FILE_TYPE_PRESETS[fileTypePreset];

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    onFilesSelected(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      onFilesSelected(files);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        "border-border-400 bg-background-50 relative w-full rounded-md border border-dashed transition-colors",
        isDragging && "border-primary-600 bg-primary-50",
      )}>
      <div className="flex w-full flex-col items-center gap-2 overflow-clip rounded-[inherit] px-2 py-4 md:gap-3 md:px-0 md:py-5">
        <CloudUploadIcon className="size-4 shrink-0 md:size-5" />

        <div className="flex w-full flex-col items-center gap-1 md:gap-2">
          <div className="flex w-full items-start justify-center gap-1">
            <p className="text-typography-900 text-center text-xs leading-normal font-medium md:text-sm">
              Drag and drop your document here
            </p>
          </div>
          <p className="text-typography-600 w-full px-2 text-center text-[10px] leading-normal font-normal md:text-xs">
            {fileConfig.label} — up to {maxFileSizeMB}MB
          </p>
        </div>

        <Button
          onClick={handleBrowseClick}
          variant="outline"
          size="sm"
          className="border-tertiary-800 text-typography-800 hover:bg-background-50 flex h-7 cursor-pointer items-center justify-center gap-2 rounded border px-3 text-xs leading-normal font-medium transition-colors md:h-8 md:px-3.5">
          Browse Files
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={fileConfig.extensions.join(",")}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}
